<template>
  <v-dialog v-model="dialogVisible" max-width="700px" height="90vh" >
    <v-card class="wage-tax-req-modal">
      <!-- 헤더 -->
      <div class="modal-header">
        <div class="header-icon">
          <v-icon size="28" color="#1e3a8a">mdi-file-document-edit</v-icon>
        </div>
        <div class="header-content">
          <h2>임금신고 요청</h2>
          <p>{{ yearMonth ? `${yearMonth.substring(0, 4)}년 ${yearMonth.substring(4, 6)}월` : '' }} 임금신고</p>
        </div>
        <button class="close-btn" @click="handleClose" :disabled="isSubmitting">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>

      <!-- 이미 요청된 상태 -->
      <div v-if="currentRequest" class="modal-body">
        <div class="request-status-card" :class="getStatusClass(currentRequest.wtr_status)">
          <div class="status-icon">
            <v-icon size="32" :color="getStatusColor(currentRequest.wtr_status)">
              {{ getStatusIcon(currentRequest.wtr_status) }}
            </v-icon>
          </div>
          <div class="status-content">
            <h3>{{ getStatusTitle(currentRequest.wtr_status) }}</h3>
            <p class="status-date">
              {{ currentRequest.wtr_status === '요청' ? '요청일시' : 
                 currentRequest.wtr_status === '취소' ? '취소일시' : '처리일시' }}: 
              {{ formatDateTime(currentRequest.wtr_status === '요청' ? currentRequest.wtr_requested_at : 
                               currentRequest.wtr_status === '취소' ? currentRequest.wtr_cancelled_at : 
                               currentRequest.wtr_processed_at) }}
            </p>
          </div>
        </div>

        <!-- 요청 내용 -->
        <div class="request-detail-section">
          <h4>요청 내용</h4>
          
          <div class="detail-row">
            <span class="label">요청 유형</span>
            <span class="value">{{ currentRequest.wtr_type }}</span>
          </div>

          <div v-if="parsedNewEmployees.length > 0" class="detail-row">
            <span class="label">입사자</span>
            <div class="employee-chips">
              <span v-for="emp in parsedNewEmployees" :key="emp.emp_code" class="emp-chip new">
                {{ emp.emp_name }}
              </span>
            </div>
          </div>

          <div v-if="parsedResignedEmployees.length > 0" class="detail-row">
            <span class="label">퇴사자</span>
            <div class="employee-chips">
              <span v-for="emp in parsedResignedEmployees" :key="emp.emp_code" class="emp-chip resigned">
                {{ emp.emp_name }}
              </span>
            </div>
          </div>

          <div v-if="currentRequest.wtr_request_memo" class="detail-row">
            <span class="label">요청 메모</span>
            <span class="value memo">{{ currentRequest.wtr_request_memo }}</span>
          </div>
        </div>

        <!-- 관리자 응답 (승인/미승인 시) -->
        <div v-if="currentRequest.wtr_status === '승인' || currentRequest.wtr_status === '미승인'" class="admin-response-section">
          <h4>관리자 응답</h4>
          <div v-if="currentRequest.wtr_result_memo" class="admin-memo">
            {{ currentRequest.wtr_result_memo }}
          </div>
          <div v-else class="no-memo">메모 없음</div>
        </div>

        <!-- 취소 사유 (취소 시) -->
        <div v-if="currentRequest.wtr_status === '취소' && currentRequest.wtr_cancel_reason" class="cancel-reason-section">
          <h4>취소 사유</h4>
          <div class="cancel-memo">{{ currentRequest.wtr_cancel_reason }}</div>
        </div>
      </div>

      <!-- 새 요청 폼 -->
      <div v-else class="modal-body">
        <!-- 기관 필수 정보 미입력 - 인라인 수정 -->
        <div v-if="missingCenterFields.length > 0" class="warning-section center-warning">
          <div class="warning-header">
            <v-icon size="24" color="#f59e0b">mdi-domain</v-icon>
            <span>기관 필수 정보 미입력 ({{ missingCenterFields.length }}개)</span>
          </div>
          <p class="warning-desc">아래에서 기관 정보를 직접 입력하여 저장할 수 있습니다.</p>
          
          <div class="center-form-grid">
            <div class="center-form-item" :class="{ 'has-error': !centerFormData.m_sisul_name }">
              <label>기관명 <span class="required">*</span></label>
              <input
                v-model="centerFormData.m_sisul_name"
                type="text"
                placeholder="기관명을 입력하세요"
                maxlength="45"
              />
            </div>
            
            <div class="center-form-item" :class="{ 'has-error': !centerFormData.m_user_name }">
              <label>대표자명 <span class="required">*</span></label>
              <input
                v-model="centerFormData.m_user_name"
                type="text"
                placeholder="대표자명을 입력하세요"
                maxlength="45"
              />
            </div>
            
            <div class="center-form-item" :class="{ 'has-error': !centerFormData.m_biz_num }">
              <label>사업자등록번호 <span class="required">*</span></label>
              <input
                v-model="centerFormData.m_biz_num"
                type="text"
                placeholder="000-00-00000"
                maxlength="12"
                @input="onBizNumInput"
              />
            </div>
            
            <div class="center-form-item full-width" :class="{ 'has-error': !centerFormData.m_sisul_call_num1 || !centerFormData.m_sisul_call_num2 || !centerFormData.m_sisul_call_num3 }">
              <label>기관 전화번호 <span class="required">*</span></label>
              <div class="phone-input-group">
                <input
                  v-model="centerFormData.m_sisul_call_num1"
                  type="text"
                  placeholder="02"
                  maxlength="4"
                  @input="(e: any) => (centerFormData.m_sisul_call_num1 = e.target.value.replace(/[^\d]/g, '').slice(0, 4))"
                />
                <span class="phone-separator">-</span>
                <input
                  v-model="centerFormData.m_sisul_call_num2"
                  type="text"
                  placeholder="1234"
                  maxlength="4"
                  @input="(e: any) => (centerFormData.m_sisul_call_num2 = e.target.value.replace(/[^\d]/g, '').slice(0, 4))"
                />
                <span class="phone-separator">-</span>
                <input
                  v-model="centerFormData.m_sisul_call_num3"
                  type="text"
                  placeholder="5678"
                  maxlength="4"
                  @input="(e: any) => (centerFormData.m_sisul_call_num3 = e.target.value.replace(/[^\d]/g, '').slice(0, 4))"
                />
              </div>
            </div>
            
            <div class="center-form-item full-width" :class="{ 'has-error': !centerFormData.m_addr }">
              <label>주소 <span class="required">*</span></label>
              <div class="address-input-group">
                <input
                  v-model="centerFormData.m_addr"
                  type="text"
                  placeholder="주소 검색 버튼을 클릭하세요"
                  maxlength="100"
                  readonly
                  @click="openAddressSearch"
                />
                <button 
                  type="button" 
                  class="address-search-btn"
                  @click="openAddressSearch"
                  :disabled="isAddressLoading"
                >
                  <v-progress-circular v-if="isAddressLoading" indeterminate size="14" width="2" color="white" />
                  <v-icon v-else size="16">mdi-magnify</v-icon>
                  검색
                </button>
              </div>
            </div>

            <div class="center-form-item full-width">
              <label>상세주소</label>
              <input
                v-model="centerFormData.m_addr_detail"
                type="text"
                placeholder="상세주소를 입력하세요 (동/호수 등)"
                maxlength="100"
              />
            </div>
          </div>
          
          <button 
            type="button" 
            class="save-center-btn" 
            @click="saveCenterInfo"
            :disabled="isSavingCenter || missingCenterFields.length > 0 && !centerFormData.m_sisul_name && !centerFormData.m_user_name"
          >
            <v-progress-circular v-if="isSavingCenter" indeterminate size="16" width="2" color="white" class="mr-1" />
            <v-icon v-else size="16" class="mr-1">mdi-content-save</v-icon>
            기관 정보 저장
          </button>
        </div>

        <!-- 주민등록번호 미등록 경고 및 인라인 수정 -->
        <div v-if="invalidBirthNumberEmployees.length > 0" class="warning-section">
          <div class="warning-header">
            <v-icon size="24" color="#ef4444">mdi-alert-circle</v-icon>
            <span>주민등록번호 미등록 사원 ({{ invalidBirthNumberEmployees.length }}명)</span>
          </div>
          <p class="warning-desc">아래에서 주민등록번호를 직접 입력하여 등록할 수 있습니다.</p>
          <p class="warning-desc">생년월일과 주민등록번호 앞자리가 다른 사원이 있을 수 있으니 주의해주세요.</p>
          <div class="birth-number-edit-list">
            <div v-for="emp in invalidBirthNumberEmployees" :key="emp.emp_code" class="birth-number-edit-item">
              <div class="emp-info-row">
                <v-icon size="18" color="#ef4444">mdi-account-alert</v-icon>
                <span class="emp-name">{{ emp.emp_name }}</span>
                <span class="emp-birth-tag" v-if="emp.emp_birth">({{ formatBirth(emp.emp_birth) }})</span>
              </div>
              <div class="birth-number-input-row">
                <input
                  v-model="birthNumberInputs[emp.emp_code].front"
                  type="text"
                  class="birth-input front"
                  maxlength="6"
                  placeholder="앞 6자리"
                  @input="onBirthFrontInput(emp.emp_code, $event)"
                />
                <span class="birth-separator">-</span>
                <input
                  v-model="birthNumberInputs[emp.emp_code].back"
                  type="text"
                  class="birth-input back"
                  maxlength="7"
                  placeholder="뒤 7자리"
                  @input="onBirthBackInput(emp.emp_code, $event)"
                />
                <button 
                  v-if="emp.emp_birth && !birthNumberInputs[emp.emp_code].front"
                  type="button"
                  class="fill-btn"
                  @click="fillBirthFront(emp)"
                  title="생년월일에서 앞자리 채우기"
                >
                  <v-icon size="16">mdi-auto-fix</v-icon>
                </button>
                <button 
                  type="button"
                  class="save-btn"
                  :disabled="!isValidInput(emp.emp_code) || savingEmpCode === emp.emp_code"
                  @click="saveBirthNumber(emp)"
                >
                  <v-progress-circular v-if="savingEmpCode === emp.emp_code" indeterminate size="14" width="2" color="white" />
                  <v-icon v-else size="16">mdi-check</v-icon>
                </button>
              </div>
              <div v-if="birthNumberInputs[emp.emp_code].front || birthNumberInputs[emp.emp_code].back" class="input-preview">
                {{ birthNumberInputs[emp.emp_code].front || '______' }}-{{ birthNumberInputs[emp.emp_code].back || '_______' }}
                <span v-if="isValidInput(emp.emp_code)" class="valid-mark">✓ 유효</span>
                <span v-else class="invalid-mark">{{ getValidationError(emp.emp_code) }}</span>
              </div>
            </div>
          </div>
          
          <button 
            type="button" 
            class="save-all-birth-btn" 
            @click="saveAllBirthNumbers"
            :disabled="isSavingAllBirthNumbers || validBirthNumberCount === 0"
          >
            <v-progress-circular v-if="isSavingAllBirthNumbers" indeterminate size="16" width="2" color="white" class="mr-1" />
            <v-icon v-else size="16" class="mr-1">mdi-content-save-all</v-icon>
            주민등록번호 일괄 저장 ({{ validBirthNumberCount }}명)
          </button>
        </div>

        <!-- 요청 유형 선택 -->
        <div class="form-section">
          <label class="form-label">요청 유형</label>
          <v-select
            v-model="formData.type"
            :items="requestTypes"
            item-title="text"
            item-value="value"
            variant="outlined"
            density="comfortable"
            hide-details
            class="type-select"
          />
        </div>

        <!-- 입사자 선택 -->
        <div class="form-section">
          <div class="section-header">
            <label class="form-label">입사자 선택 <span class="selected-count" v-if="formData.newEmployees.length > 0">({{ formData.newEmployees.length }}명 선택)</span></label>
            <span class="section-hint">입사자로 등록할 직원을 선택하세요</span>
          </div>
          <div class="employee-selection-grid">
            <div 
              v-for="emp in availableNewEmployees" 
              :key="'new-' + emp.emp_code"
              class="employee-card selectable"
              :class="{ 'selected': isSelectedNew(emp.emp_code) }"
              @click="toggleNewEmployee(emp)"
            >
              <div class="emp-avatar new">
                <v-icon size="18" color="white">mdi-account-plus</v-icon>
              </div>
              <div class="emp-info">
                <span class="emp-name">{{ emp.emp_name }} <span class="emp-birth" v-if="emp.emp_birth">({{ formatBirth(emp.emp_birth) }})</span></span>
                <span class="emp-date">입사: {{ formatDate(emp.emp_in_date) }}</span>
              </div>
              <div class="check-mark" v-if="isSelectedNew(emp.emp_code)">
                <v-icon size="18" color="#10b981">mdi-check-circle</v-icon>
              </div>
            </div>
            <div v-if="availableNewEmployees.length === 0" class="no-employee-notice">
              선택 가능한 사원이 없습니다
            </div>
          </div>
        </div>

        <!-- 퇴사자 선택 -->
        <div class="form-section">
          <div class="section-header">
            <label class="form-label">퇴사자 선택 <span class="selected-count" v-if="formData.resignedEmployees.length > 0">({{ formData.resignedEmployees.length }}명 선택)</span></label>
            <span class="section-hint">퇴사자로 등록할 직원을 선택하세요</span>
          </div>
          <div class="employee-selection-grid">
            <div 
              v-for="emp in availableResignedEmployees" 
              :key="'resigned-' + emp.emp_code"
              class="employee-card selectable"
              :class="{ 'selected': isSelectedResigned(emp.emp_code) }"
              @click="toggleResignedEmployee(emp)"
            >
              <div class="emp-avatar resigned">
                <v-icon size="18" color="white">mdi-account-minus</v-icon>
              </div>
              <div class="emp-info">
                <span class="emp-name">{{ emp.emp_name }} <span class="emp-birth" v-if="emp.emp_birth">({{ formatBirth(emp.emp_birth) }})</span></span>
                <span class="emp-date">{{ emp.emp_out_date ? '퇴사: ' + formatDate(emp.emp_out_date) : '재직중' }}</span>
              </div>
              <div class="check-mark" v-if="isSelectedResigned(emp.emp_code)">
                <v-icon size="18" color="#10b981">mdi-check-circle</v-icon>
              </div>
            </div>
            <div v-if="availableResignedEmployees.length === 0" class="no-employee-notice">
              선택 가능한 사원이 없습니다
            </div>
          </div>
        </div>

        <!-- 메모 -->
        <div class="form-section">
          <label class="form-label">요청 메모</label>
          <v-textarea
            v-model="formData.memo"
            variant="outlined"
            placeholder="관리자에게 전달할 메모를 입력하세요 (선택사항)"
            rows="3"
            hide-details
            class="memo-textarea"
          />
        </div>
      </div>

      <!-- 푸터 -->
      <div class="modal-footer">
        <template v-if="currentRequest">
          <!-- 요청 상태일 때만 취소 가능 -->
          <template v-if="currentRequest.wtr_status === '요청'">
            <button class="btn-cancel-request" @click="showCancelDialog = true" :disabled="isSubmitting">
              <v-icon size="18" class="mr-1">mdi-close-circle</v-icon>
              요청 취소
            </button>
          </template>
          <button class="btn-close" @click="handleClose">
            닫기
          </button>
        </template>
        <template v-else>
          <button class="btn-cancel" @click="handleClose" :disabled="isSubmitting">
            취소
          </button>
          <button class="btn-submit" @click="handleSubmit" :disabled="isSubmitting || !canSubmitRequest">
            <v-progress-circular v-if="isSubmitting" indeterminate size="18" width="2" color="white" class="mr-2" />
            <v-icon v-else size="18" class="mr-1">mdi-send</v-icon>
            {{ canSubmitRequest ? '요청하기' : '필수정보 누락' }}
          </button>
        </template>
      </div>

      <!-- 요청 취소 확인 다이얼로그 -->
      <v-dialog v-model="showCancelDialog" max-width="400px">
        <v-card class="cancel-confirm-dialog">
          <div class="dialog-header warning">
            <v-icon size="40" color="#f59e0b">mdi-alert-circle</v-icon>
            <h3>요청 취소</h3>
          </div>
          <div class="dialog-body">
            <p>임금신고 요청을 취소하시겠습니까?</p>
            <v-textarea
              v-model="cancelReason"
              variant="outlined"
              placeholder="취소 사유를 입력하세요 (선택사항)"
              rows="2"
              hide-details
              class="cancel-reason-input"
            />
          </div>
          <div class="dialog-footer">
            <button class="btn-dialog-cancel" @click="showCancelDialog = false" :disabled="isCancelling">취소</button>
            <button class="btn-dialog-confirm warning" @click="handleCancelRequest" :disabled="isCancelling">
              <v-progress-circular v-if="isCancelling" indeterminate size="16" width="2" color="white" class="mr-1" />
              요청 취소
            </button>
          </div>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';

interface Employee {
  emp_code: string;
  emp_name: string;
  emp_birth?: string;
  emp_birth_number?: string;
  emp_job?: string;
  emp_in_date?: string;
  emp_out_date?: string;
}

interface WageTaxRequest {
  wtr_idx: number;
  m_sisul_code: string;
  wtr_leb_ym: string;
  wtr_type: string;
  wtr_status: string;
  wtr_new_employees?: string;
  wtr_resigned_employees?: string;
  wtr_request_memo?: string;
  wtr_requested_by?: string;
  wtr_requested_at?: string;
  wtr_cancelled_by?: string;
  wtr_cancelled_at?: string;
  wtr_cancel_reason?: string;
  wtr_result_memo?: string;
  wtr_processed_by?: string;
  wtr_processed_at?: string;
}

const props = defineProps<{
  modelValue: boolean;
  yearMonth: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'request-submitted'): void;
  (e: 'request-cancelled'): void;
}>();

const store = useStore();

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

// 상태
const isLoading = ref(false);
const isSubmitting = ref(false);
const isCancelling = ref(false);
const showCancelDialog = ref(false);
const cancelReason = ref('');
const currentRequest = ref<WageTaxRequest | null>(null);
const employees = ref<Employee[]>([]);

// 주민등록번호 인라인 입력 상태
const birthNumberInputs = ref<Record<string, { front: string; back: string }>>({});
const savingEmpCode = ref<string | null>(null);
const isSavingAllBirthNumbers = ref(false);

// 기관 정보 인라인 입력 상태
const centerFormData = ref({
  m_sisul_name: '',
  m_user_name: '',
  m_biz_num: '',
  m_addr: '',
  m_addr_detail: '',
  m_sisul_call_num1: '',
  m_sisul_call_num2: '',
  m_sisul_call_num3: ''
});
const isSavingCenter = ref(false);
const isAddressLoading = ref(false);

// 폼 데이터
const formData = ref({
  type: '임금신고',
  newEmployees: [] as Employee[],
  resignedEmployees: [] as Employee[],
  memo: ''
});

// 요청 유형 목록 (추후 확장 가능)
const requestTypes = [
  { text: '임금신고', value: '임금신고' }
];

// 입사자로 선택 가능한 사원 (모든 사원 - 같은 달에 입사/퇴사 모두 가능)
const availableNewEmployees = computed(() => {
  return employees.value;
});

// 퇴사자로 선택 가능한 사원 (모든 사원 - 같은 달에 입사/퇴사 모두 가능)
const availableResignedEmployees = computed(() => {
  return employees.value;
});

// 주민등록번호 유효성 검증
const isValidBirthNumber = (birthNumber?: string) => {
  if (!birthNumber) return false;
  const cleaned = birthNumber.replace(/-/g, '');
  if (cleaned.length !== 13) return false;
  
  // 앞자리 6자리 (YYMMDD) 검증
  const front = cleaned.substring(0, 6);
  const month = parseInt(front.substring(2, 4), 10);
  const day = parseInt(front.substring(4, 6), 10);
  
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  
  // 뒷자리 첫 번째 숫자 (성별코드) 검증: 1~8
  const genderCode = parseInt(cleaned.charAt(6), 10);
  if (genderCode < 1 || genderCode > 8) return false;
  
  return true;
};

// 주민등록번호가 불완전한 사원 목록
const invalidBirthNumberEmployees = computed(() => {
  return employees.value.filter(emp => !isValidBirthNumber(emp.emp_birth_number));
});

// 유효한 입력이 완료된 주민등록번호 개수
const validBirthNumberCount = computed(() => {
  return Object.keys(birthNumberInputs.value).filter(empCode => isValidInput(empCode)).length;
});

// 임금신고 요청 가능 여부 (모든 사원의 주민등록번호 + 기관 필수정보)
const canSubmitRequest = computed(() => {
  return invalidBirthNumberEmployees.value.length === 0 && missingCenterFields.value.length === 0;
});

// 기관 필수 정보 검증
const centerInfo = computed(() => store.state.user);

const missingCenterFields = computed((): { field: string; label: string }[] => {
  const user = centerInfo.value;
  if (!user) return [{ field: 'none', label: '기관 정보 없음' }];
  
  const missing: { field: string; label: string }[] = [];
  
  if (!user.m_sisul_name?.trim()) {
    missing.push({ field: 'm_sisul_name', label: '기관명' });
  }
  if (!user.m_user_name?.trim()) {
    missing.push({ field: 'm_user_name', label: '대표자명' });
  }
  if (!user.m_biz_num?.trim()) {
    missing.push({ field: 'm_biz_num', label: '사업자등록번호' });
  }
  if (!user.m_addr?.trim()) {
    missing.push({ field: 'm_addr', label: '주소' });
  }
  if (!user.m_sisul_call_num1?.trim() || !user.m_sisul_call_num2?.trim() || !user.m_sisul_call_num3?.trim()) {
    missing.push({ field: 'm_sisul_call_num', label: '기관 전화번호' });
  }
  
  return missing;
});

// 주민등록번호 입력 상태 초기화
const initBirthNumberInputs = () => {
  const inputs: Record<string, { front: string; back: string }> = {};
  employees.value.forEach(emp => {
    if (!isValidBirthNumber(emp.emp_birth_number)) {
      // 앞자리, 뒷자리 모두 빈값으로 초기화 (생년월일에서 자동으로 채우지 않음)
      inputs[emp.emp_code] = { front: '', back: '' };
    }
  });
  birthNumberInputs.value = inputs;
};

// 생년월일에서 주민등록번호 앞자리 추출 (YYYYMMDD → YYMMDD)
const extractBirthFront = (birth: string): string => {
  if (!birth) return '';
  const digitsOnly = birth.replace(/[^\d]/g, '');
  if (digitsOnly.length >= 8) {
    return digitsOnly.substring(2, 8);
  }
  return '';
};

// 주민등록번호 앞자리 입력 핸들러
const onBirthFrontInput = (empCode: string, e: Event) => {
  const target = e.target as HTMLInputElement;
  const value = target.value.replace(/[^\d]/g, '');
  if (birthNumberInputs.value[empCode]) {
    birthNumberInputs.value[empCode].front = value;
  }
};

// 주민등록번호 뒷자리 입력 핸들러
const onBirthBackInput = (empCode: string, e: Event) => {
  const target = e.target as HTMLInputElement;
  const value = target.value.replace(/[^\d]/g, '');
  if (birthNumberInputs.value[empCode]) {
    birthNumberInputs.value[empCode].back = value;
  }
};

// 생년월일에서 앞자리 자동 채우기
const fillBirthFront = (emp: Employee) => {
  if (emp.emp_birth && birthNumberInputs.value[emp.emp_code]) {
    birthNumberInputs.value[emp.emp_code].front = extractBirthFront(emp.emp_birth);
  }
};

// 입력 유효성 검증 (앞6자리 + 뒤7자리 + 실제 유효한 값)
const isValidInput = (empCode: string) => {
  const input = birthNumberInputs.value[empCode];
  if (!input) return false;
  if (input.front.length !== 6 || input.back.length !== 7) return false;
  
  // 앞자리 (YYMMDD) 검증
  const month = parseInt(input.front.substring(2, 4), 10);
  const day = parseInt(input.front.substring(4, 6), 10);
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  
  // 뒷자리 첫 번째 숫자 (성별코드) 검증: 1~8
  const genderCode = parseInt(input.back.charAt(0), 10);
  if (genderCode < 1 || genderCode > 8) return false;
  
  return true;
};

// 유효성 검증 에러 메시지
const getValidationError = (empCode: string) => {
  const input = birthNumberInputs.value[empCode];
  if (!input) return '입력 필요';
  
  if (input.front.length !== 6) return `앞자리 ${input.front.length}/6자리`;
  if (input.back.length !== 7) return `뒷자리 ${input.back.length}/7자리`;
  
  const month = parseInt(input.front.substring(2, 4), 10);
  const day = parseInt(input.front.substring(4, 6), 10);
  if (month < 1 || month > 12) return `월(${month})이 잘못됨 (1~12)`;
  if (day < 1 || day > 31) return `일(${day})이 잘못됨 (1~31)`;
  
  const genderCode = parseInt(input.back.charAt(0), 10);
  if (genderCode < 1 || genderCode > 8) return `성별코드(${genderCode})가 잘못됨 (1~8)`;
  
  return '알 수 없는 오류';
};

// 주민등록번호 저장
const saveBirthNumber = async (emp: Employee) => {
  if (!isValidInput(emp.emp_code)) return;
  
  const input = birthNumberInputs.value[emp.emp_code];
  const fullBirthNumber = input.front + input.back;
  
  savingEmpCode.value = emp.emp_code;
  
  try {
    const response = await axios.post('/api/calc/employee', {
      emp_code: emp.emp_code,
      m_sisul_code: store.state.user?.m_sisul_code,
      emp_name: emp.emp_name,
      emp_birth: emp.emp_birth,
      emp_birth_number: fullBirthNumber,
      emp_job: emp.emp_job,
      emp_in_date: emp.emp_in_date,
      emp_out_date: emp.emp_out_date
    }, {
      withCredentials: true
    });
    
    if (response.data.success) {
      // 로컬 사원 데이터 업데이트
      const empIndex = employees.value.findIndex(e => e.emp_code === emp.emp_code);
      if (empIndex >= 0) {
        employees.value[empIndex].emp_birth_number = fullBirthNumber;
      }
      
      // 입력 상태에서 제거
      delete birthNumberInputs.value[emp.emp_code];
      
      store.commit('showSnackbar', {
        text: `${emp.emp_name}님의 주민등록번호가 저장되었습니다.`,
        color: 'success'
      });
    } else {
      throw new Error(response.data.message || '저장 실패');
    }
  } catch (error: any) {
    console.error('주민등록번호 저장 오류:', error);
    store.commit('showSnackbar', {
      text: error.response?.data?.message || '주민등록번호 저장 중 오류가 발생했습니다.',
      color: 'error'
    });
  } finally {
    savingEmpCode.value = null;
  }
};

// 주민등록번호 일괄 저장
const saveAllBirthNumbers = async () => {
  const validEmployees = invalidBirthNumberEmployees.value.filter(emp => isValidInput(emp.emp_code));
  
  if (validEmployees.length === 0) {
    store.commit('showSnackbar', {
      text: '저장할 유효한 주민등록번호가 없습니다.',
      color: 'warning'
    });
    return;
  }
  
  isSavingAllBirthNumbers.value = true;
  let successCount = 0;
  let failCount = 0;
  
  try {
    for (const emp of validEmployees) {
      const input = birthNumberInputs.value[emp.emp_code];
      const fullBirthNumber = input.front + input.back;
      
      try {
        const response = await axios.post('/api/calc/employee', {
          emp_code: emp.emp_code,
          m_sisul_code: store.state.user?.m_sisul_code,
          emp_name: emp.emp_name,
          emp_birth: emp.emp_birth,
          emp_birth_number: fullBirthNumber,
          emp_job: emp.emp_job,
          emp_in_date: emp.emp_in_date,
          emp_out_date: emp.emp_out_date
        }, {
          withCredentials: true
        });
        
        if (response.data.success) {
          // 로컬 사원 데이터 업데이트
          const empIndex = employees.value.findIndex(e => e.emp_code === emp.emp_code);
          if (empIndex >= 0) {
            employees.value[empIndex].emp_birth_number = fullBirthNumber;
          }
          
          // 입력 상태에서 제거
          delete birthNumberInputs.value[emp.emp_code];
          successCount++;
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
    }
    
    if (successCount > 0) {
      store.commit('showSnackbar', {
        text: `${successCount}명의 주민등록번호가 저장되었습니다.${failCount > 0 ? ` (${failCount}명 실패)` : ''}`,
        color: failCount > 0 ? 'warning' : 'success'
      });
    } else {
      store.commit('showSnackbar', {
        text: '주민등록번호 저장에 실패했습니다.',
        color: 'error'
      });
    }
  } catch (error: any) {
    console.error('주민등록번호 일괄 저장 오류:', error);
    store.commit('showSnackbar', {
      text: '주민등록번호 일괄 저장 중 오류가 발생했습니다.',
      color: 'error'
    });
  } finally {
    isSavingAllBirthNumbers.value = false;
  }
};

// 저장된 입사자/퇴사자 파싱
const parsedNewEmployees = computed(() => {
  if (!currentRequest.value?.wtr_new_employees) return [];
  try {
    return JSON.parse(currentRequest.value.wtr_new_employees);
  } catch {
    return [];
  }
});

const parsedResignedEmployees = computed(() => {
  if (!currentRequest.value?.wtr_resigned_employees) return [];
  try {
    return JSON.parse(currentRequest.value.wtr_resigned_employees);
  } catch {
    return [];
  }
});

// 선택 상태 확인
const isSelectedNew = (empCode: string) => {
  return formData.value.newEmployees.some(e => e.emp_code === empCode);
};

const isSelectedResigned = (empCode: string) => {
  return formData.value.resignedEmployees.some(e => e.emp_code === empCode);
};

// 입사자 토글
const toggleNewEmployee = (emp: Employee) => {
  const idx = formData.value.newEmployees.findIndex(e => e.emp_code === emp.emp_code);
  if (idx >= 0) {
    formData.value.newEmployees.splice(idx, 1);
  } else {
    formData.value.newEmployees.push(emp);
  }
};

// 퇴사자 토글
const toggleResignedEmployee = (emp: Employee) => {
  const idx = formData.value.resignedEmployees.findIndex(e => e.emp_code === emp.emp_code);
  if (idx >= 0) {
    formData.value.resignedEmployees.splice(idx, 1);
  } else {
    formData.value.resignedEmployees.push(emp);
  }
};

// 상태별 스타일
const getStatusClass = (status: string) => {
  switch (status) {
    case '요청': return 'pending';
    case '승인': return 'approved';
    case '미승인': return 'rejected';
    case '취소': return 'cancelled';
    default: return '';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case '요청': return '#3b82f6';
    case '승인': return '#10b981';
    case '미승인': return '#ef4444';
    case '취소': return '#6b7280';
    default: return '#6b7280';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case '요청': return 'mdi-clock-outline';
    case '승인': return 'mdi-check-circle';
    case '미승인': return 'mdi-close-circle';
    case '취소': return 'mdi-cancel';
    default: return 'mdi-help-circle';
  }
};

const getStatusTitle = (status: string) => {
  switch (status) {
    case '요청': return '요청 대기중';
    case '승인': return '승인 완료';
    case '미승인': return '미승인';
    case '취소': return '요청 취소됨';
    default: return status;
  }
};

// 날짜 포맷
const formatDate = (date?: string) => {
  if (!date) return '-';
  if (date.includes('-')) return date;
  if (date.length === 8) {
    return `${date.substring(0, 4)}-${date.substring(4, 6)}-${date.substring(6, 8)}`;
  }
  return date;
};

// 생년월일 포맷 (예: 19900101 → 90.01.01)
const formatBirth = (birth?: string) => {
  if (!birth) return '';
  const cleaned = birth.replace(/-/g, '');
  if (cleaned.length >= 8) {
    return `${cleaned.substring(2, 4)}.${cleaned.substring(4, 6)}.${cleaned.substring(6, 8)}`;
  }
  return birth;
};

const formatDateTime = (datetime?: string) => {
  if (!datetime) return '-';
  const d = new Date(datetime);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// 입사자/퇴사자 자동 선택 (해당 월에 입사/퇴사한 사원)
const autoSelectEmployees = () => {
  if (!props.yearMonth) return;
  
  const targetYm = props.yearMonth; // YYYYMM 형식
  
  // 폼 데이터 초기화
  formData.value.newEmployees = [];
  formData.value.resignedEmployees = [];
  
  employees.value.forEach(emp => {
    // 입사일 체크 (YYYYMMDD 또는 YYYY-MM-DD 형식)
    if (emp.emp_in_date) {
      const inDateYm = emp.emp_in_date.replace(/-/g, '').substring(0, 6);
      if (inDateYm === targetYm) {
        formData.value.newEmployees.push(emp);
      }
    }
    
    // 퇴사일 체크 (YYYYMMDD 또는 YYYY-MM-DD 형식)
    // 같은 달에 입사와 퇴사를 함께 하는 경우도 있으므로 독립적으로 체크
    if (emp.emp_out_date) {
      const outDateYm = emp.emp_out_date.replace(/-/g, '').substring(0, 6);
      if (outDateYm === targetYm) {
        formData.value.resignedEmployees.push(emp);
      }
    }
  });
};

// 데이터 로드
const loadData = async () => {
  const user = store.state.user;
  if (!user?.m_sisul_code || !props.yearMonth) return;

  isLoading.value = true;
  try {
    // 기존 요청 조회
    const requestRes = await axios.get('/api/wage-process/tax-request', {
      params: {
        sisulCode: user.m_sisul_code,
        yearMonth: props.yearMonth
      }
    });

    if (requestRes.data.success && requestRes.data.data) {
      currentRequest.value = requestRes.data.data;
    } else {
      currentRequest.value = null;
    }

    // 사원 목록 조회
    const empRes = await axios.get(`/api/emp/facility/${user.m_sisul_code}`);
    if (empRes.data.success) {
      employees.value = empRes.data.data || [];
      // 주민등록번호 입력 상태 초기화
      initBirthNumberInputs();
      // 기관 정보 폼 초기화
      initCenterFormData();
      // 입사자/퇴사자 자동 선택 (기존 요청이 없는 경우에만)
      if (!currentRequest.value) {
        autoSelectEmployees();
      }
    }
  } catch (error) {
    console.error('데이터 로드 오류:', error);
  } finally {
    isLoading.value = false;
  }
};

// 요청 제출
const handleSubmit = async () => {
  const user = store.state.user;
  if (!user?.m_sisul_code || !props.yearMonth) return;

  isSubmitting.value = true;
  try {
    const response = await axios.post('/api/wage-process/tax-request', {
      sisulCode: user.m_sisul_code,
      yearMonth: props.yearMonth,
      type: formData.value.type,
      newEmployees: formData.value.newEmployees.map(e => ({ emp_code: e.emp_code, emp_name: e.emp_name })),
      resignedEmployees: formData.value.resignedEmployees.map(e => ({ emp_code: e.emp_code, emp_name: e.emp_name })),
      memo: formData.value.memo,
      requestedBy: user.m_id
    });

    if (response.data.success) {
      store.commit('showSnackbar', {
        text: '임금신고 요청이 완료되었습니다.',
        color: 'success'
      });
      emit('request-submitted');
      await loadData();
    } else {
      throw new Error(response.data.message || '요청 실패');
    }
  } catch (error: any) {
    console.error('요청 제출 오류:', error);
    store.commit('showSnackbar', {
      text: error.response?.data?.message || '요청 중 오류가 발생했습니다.',
      color: 'error'
    });
  } finally {
    isSubmitting.value = false;
  }
};

// 요청 취소
const handleCancelRequest = async () => {
  const user = store.state.user;
  if (!user?.m_sisul_code || !currentRequest.value) return;

  isCancelling.value = true;
  try {
    const response = await axios.post('/api/wage-process/tax-request/cancel', {
      sisulCode: user.m_sisul_code,
      yearMonth: props.yearMonth,
      cancelReason: cancelReason.value,
      cancelledBy: user.m_id
    });

    if (response.data.success) {
      store.commit('showSnackbar', {
        text: '요청이 취소되었습니다.',
        color: 'success'
      });
      showCancelDialog.value = false;
      cancelReason.value = '';
      emit('request-cancelled');
      await loadData();
    } else {
      throw new Error(response.data.message || '취소 실패');
    }
  } catch (error: any) {
    console.error('요청 취소 오류:', error);
    store.commit('showSnackbar', {
      text: error.response?.data?.message || '취소 중 오류가 발생했습니다.',
      color: 'error'
    });
  } finally {
    isCancelling.value = false;
  }
};

// 닫기
const handleClose = () => {
  dialogVisible.value = false;
  // 폼 초기화
  formData.value = {
    type: '임금신고',
    newEmployees: [],
    resignedEmployees: [],
    memo: ''
  };
  cancelReason.value = '';
};

// 기관 정보 폼 초기화
const initCenterFormData = () => {
  const user = store.state.user;
  if (user) {
    centerFormData.value = {
      m_sisul_name: user.m_sisul_name || '',
      m_user_name: user.m_user_name || '',
      m_biz_num: user.m_biz_num || '',
      m_addr: user.m_addr || '',
      m_addr_detail: user.m_addr_detail || '',
      m_sisul_call_num1: user.m_sisul_call_num1 || '',
      m_sisul_call_num2: user.m_sisul_call_num2 || '',
      m_sisul_call_num3: user.m_sisul_call_num3 || ''
    };
  }
};

// Daum 우편번호 검색 스크립트 로드
let daumScriptLoading: Promise<void> | null = null;

const loadDaumPostcodeScript = async (): Promise<void> => {
  if ((window as any).daum?.Postcode) return;
  if (daumScriptLoading) {
    await daumScriptLoading;
    return;
  }
  daumScriptLoading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Daum Postcode script load failed'));
    document.head.appendChild(script);
  });
  await daumScriptLoading;
};

// 주소 검색 팝업 열기
const openAddressSearch = async () => {
  isAddressLoading.value = true;
  try {
    await loadDaumPostcodeScript();
    const Postcode = (window as any).daum?.Postcode;
    if (!Postcode) throw new Error('Daum Postcode not loaded');
    
    const postcode = new Postcode({
      oncomplete: (data: any) => {
        const road = data.roadAddress;
        const jibun = data.jibunAddress;
        const selected = road && road.length > 0 ? road : jibun;
        centerFormData.value.m_addr = selected || '';
        isAddressLoading.value = false;
      },
      onclose: () => {
        isAddressLoading.value = false;
      }
    });
    postcode.open();
  } catch (e) {
    console.error('주소 검색 오류:', e);
    isAddressLoading.value = false;
    store.commit('showSnackbar', {
      text: '주소 검색을 불러올 수 없습니다.',
      color: 'error'
    });
  }
};

// 기관 정보 저장
const saveCenterInfo = async () => {
  isSavingCenter.value = true;
  
  try {
    const response = await axios.put('/api/auth/account', {
      ...store.state.user,
      ...centerFormData.value
    }, {
      withCredentials: true
    });
    
    if (response.data.success) {
      // 스토어의 user 정보 업데이트
      store.commit('setUser', {
        ...store.state.user,
        ...centerFormData.value
      });
      
      store.commit('showSnackbar', {
        text: '기관 정보가 저장되었습니다.',
        color: 'success'
      });
    } else {
      throw new Error(response.data.message || '저장 실패');
    }
  } catch (error: any) {
    console.error('기관 정보 저장 오류:', error);
    store.commit('showSnackbar', {
      text: error.response?.data?.message || '기관 정보 저장 중 오류가 발생했습니다.',
      color: 'error'
    });
  } finally {
    isSavingCenter.value = false;
  }
};

// 사업자등록번호 포맷 (000-00-00000)
const onBizNumInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const digits = target.value.replace(/[^\d]/g, '').slice(0, 10);
  
  let formatted = digits;
  if (digits.length > 3 && digits.length <= 5) {
    formatted = `${digits.slice(0,3)}-${digits.slice(3)}`;
  } else if (digits.length > 5) {
    formatted = `${digits.slice(0,3)}-${digits.slice(3,5)}-${digits.slice(5)}`;
  }
  
  centerFormData.value.m_biz_num = formatted;
};

// 다이얼로그 열릴 때 데이터 로드
watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    loadData();
  }
});

// props.yearMonth 변경 시 데이터 재로드
watch(() => props.yearMonth, () => {
  if (props.modelValue) {
    loadData();
  }
});

// 현재 요청 상태 expose (부모 컴포넌트에서 접근 가능)
defineExpose({
  currentRequest,
  loadData
});
</script>

<style lang="scss" scoped>
.wage-tax-req-modal {
  border-radius: 16px !important;
  overflow: hidden;

  .modal-header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 24px;
    border-bottom: 1px solid #f1f3f5;
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);

    .header-icon {
      width: 56px;
      height: 56px;
      border-radius: 14px;
      background: white;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 8px rgba(30, 58, 138, 0.15);
    }

    .header-content {
      flex: 1;

      h2 {
        font-size: 1.25rem;
        font-weight: 700;
        color: #1e3a8a;
        margin: 0 0 4px 0;
      }

      p {
        font-size: 0.875rem;
        color: #3b82f6;
        margin: 0;
        font-weight: 500;
      }
    }

    .close-btn {
      width: 36px;
      height: 36px;
      border: none;
      background: white;
      border-radius: 10px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      color: #6b7280;

      &:hover:not(:disabled) {
        background: #f1f5f9;
        color: #1f2937;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }

  .modal-body {
    padding: 24px;
    max-height: 60vh;
    overflow-y: auto;

    .warning-section {
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 12px;
      padding: 16px;
      margin-bottom: 20px;

      &.center-warning {
        background: #fffbeb;
        border-color: #fcd34d;

        .warning-header span {
          color: #b45309;
        }

        .warning-desc {
          color: #92400e;
        }

        .center-form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          margin-bottom: 16px;
          padding: 16px;
          background: white;
          border-radius: 8px;

          @media (max-width: 600px) {
            grid-template-columns: 1fr;
          }

          .center-form-item {
            display: flex;
            flex-direction: column;
            gap: 4px;
            min-width: 0;

            &.full-width {
              grid-column: 1 / -1;
            }

            &.has-error {
              label {
                color: #dc2626;
              }

              input {
                border-color: #fca5a5;
                background: #fef2f2;
              }
            }

            label {
              font-size: 0.75rem;
              font-weight: 600;
              color: #6b7280;

              .required {
                color: #dc2626;
              }
            }

            input {
              padding: 8px 12px;
              font-size: 0.875rem;
              border: 1px solid #d1d5db;
              border-radius: 6px;
              transition: all 0.15s ease;
              width: 100%;
              box-sizing: border-box;

              &:focus {
                outline: none;
                border-color: #f59e0b;
                box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.1);
              }

              &::placeholder {
                color: #9ca3af;
              }

              &[readonly] {
                background: #f9fafb;
                cursor: pointer;
              }
            }

            .phone-input-group {
              display: flex;
              align-items: center;
              gap: 4px;
              max-width: 280px;

              input {
                flex: 1;
                text-align: center;
                min-width: 0;
                padding: 8px 6px;
              }

              .phone-separator {
                color: #6b7280;
                font-weight: 500;
                flex-shrink: 0;
              }
            }

            .address-input-group {
              display: flex;
              align-items: center;
              gap: 8px;

              input {
                flex: 1;
                min-width: 0;
              }

              .address-search-btn {
                display: flex;
                align-items: center;
                gap: 4px;
                padding: 8px 14px;
                background: #3b82f6;
                color: white;
                border: none;
                border-radius: 6px;
                font-size: 0.813rem;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.15s ease;
                white-space: nowrap;
                flex-shrink: 0;

                &:hover:not(:disabled) {
                  background: #2563eb;
                }

                &:disabled {
                  background: #9ca3af;
                  cursor: not-allowed;
                }
              }
            }
          }
        }

        .save-center-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 10px 20px;
          background: #f59e0b;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;

          &:hover:not(:disabled) {
            background: #d97706;
          }

          &:disabled {
            background: #d1d5db;
            cursor: not-allowed;
          }
        }
      }

      .warning-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;

        span {
          font-size: 0.938rem;
          font-weight: 700;
          color: #dc2626;
        }
      }

      .warning-desc {
        font-size: 0.813rem;
        color: #7f1d1d;
        margin: 0 0 12px 0;
        line-height: 1.5;
      }

      .birth-number-edit-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
        max-height: 240px;
        overflow-y: auto;
        padding: 12px;
        background: white;
        border-radius: 8px;

        .birth-number-edit-item {
          padding: 12px;
          background: #fff7ed;
          border: 1px solid #fed7aa;
          border-radius: 8px;

          .emp-info-row {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 10px;

            .emp-name {
              font-size: 0.875rem;
              font-weight: 600;
              color: #1f2937;
            }

            .emp-birth-tag {
              font-size: 0.75rem;
              font-weight: 500;
              color: #6b7280;
            }
          }

          .birth-number-input-row {
            display: flex;
            align-items: center;
            gap: 6px;

            .birth-input {
              padding: 8px 10px;
              font-size: 0.875rem;
              font-weight: 600;
              border: 1px solid #d1d5db;
              border-radius: 6px;
              text-align: center;
              transition: all 0.15s ease;

              &:focus {
                outline: none;
                border-color: #1e3a8a;
                box-shadow: 0 0 0 2px rgba(30, 58, 138, 0.1);
              }

              &.front {
                width: 90px;
                background: #f9fafb;
              }

              &.back {
                width: 100px;
              }
            }

            .birth-separator {
              font-size: 1.125rem;
              font-weight: 500;
              color: #374151;
            }

            .fill-btn {
              width: 32px;
              height: 32px;
              border: none;
              background: #3b82f6;
              color: white;
              border-radius: 6px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.15s ease;

              &:hover {
                background: #2563eb;
              }
            }

            .save-btn {
              width: 32px;
              height: 32px;
              border: none;
              background: #10b981;
              color: white;
              border-radius: 6px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.15s ease;

              &:hover:not(:disabled) {
                background: #059669;
              }

              &:disabled {
                background: #d1d5db;
                cursor: not-allowed;
              }
            }
          }

          .input-preview {
            margin-top: 8px;
            font-size: 0.75rem;
            color: #6b7280;

            .valid-mark {
              color: #10b981;
              font-weight: 600;
              margin-left: 8px;
            }

            .invalid-mark {
              color: #ef4444;
              font-weight: 500;
              margin-left: 8px;
            }
          }
        }
      }

      .save-all-birth-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 10px 20px;
        margin-top: 12px;
        background: #ef4444;
        color: white;
        border: none;
        border-radius: 8px;
        font-size: 0.875rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.15s ease;

        &:hover:not(:disabled) {
          background: #dc2626;
        }

        &:disabled {
          background: #d1d5db;
          cursor: not-allowed;
        }
      }
    }

    .request-status-card {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 20px;
      border-radius: 12px;
      margin-bottom: 20px;

      &.pending {
        background: #eff6ff;
        border: 1px solid #bfdbfe;
      }

      &.approved {
        background: #ecfdf5;
        border: 1px solid #a7f3d0;
      }

      &.rejected {
        background: #fef2f2;
        border: 1px solid #fecaca;
      }

      &.cancelled {
        background: #f9fafb;
        border: 1px solid #e5e7eb;
      }

      .status-icon {
        width: 56px;
        height: 56px;
        border-radius: 14px;
        background: white;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .status-content {
        flex: 1;

        h3 {
          font-size: 1.125rem;
          font-weight: 700;
          margin: 0 0 4px 0;
          color: #191f28;
        }

        .status-date {
          font-size: 0.813rem;
          color: #6b7280;
          margin: 0;
        }
      }
    }

    .request-detail-section,
    .admin-response-section,
    .cancel-reason-section {
      background: #f9fafb;
      border-radius: 12px;
      padding: 20px;
      margin-bottom: 16px;

      h4 {
        font-size: 0.938rem;
        font-weight: 700;
        color: #374151;
        margin: 0 0 16px 0;
        padding-bottom: 12px;
        border-bottom: 1px solid #e5e7eb;
      }

      .detail-row {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        margin-bottom: 12px;

        &:last-child {
          margin-bottom: 0;
        }

        .label {
          min-width: 80px;
          font-size: 0.813rem;
          font-weight: 600;
          color: #6b7280;
        }

        .value {
          font-size: 0.875rem;
          color: #1f2937;
          font-weight: 500;

          &.memo {
            background: white;
            padding: 10px 14px;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
            flex: 1;
            white-space: pre-wrap;
          }
        }
      }

      .employee-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;

        .emp-chip {
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 0.813rem;
          font-weight: 600;

          &.new {
            background: #dbeafe;
            color: #1e40af;
          }

          &.resigned {
            background: #fee2e2;
            color: #991b1b;
          }
        }
      }

      .admin-memo,
      .cancel-memo {
        background: white;
        padding: 14px 16px;
        border-radius: 8px;
        border: 1px solid #e5e7eb;
        font-size: 0.875rem;
        color: #374151;
        line-height: 1.6;
        white-space: pre-wrap;
      }

      .no-memo {
        font-size: 0.875rem;
        color: #9ca3af;
        font-style: italic;
      }
    }

    .form-section {
      margin-bottom: 24px;

      &:last-child {
        margin-bottom: 0;
      }

      .section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 10px;

        .section-hint {
          font-size: 0.75rem;
          color: #9ca3af;
        }
      }

      .form-label {
        display: block;
        font-size: 0.875rem;
        font-weight: 700;
        color: #374151;
        margin-bottom: 10px;

        .selected-count {
          font-weight: 600;
          color: #10b981;
        }
      }

      .type-select {
        max-width: 200px;
      }

      .employee-selection-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
        max-height: 400px;
        overflow-y: auto;
        padding: 4px;

        .employee-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            border-color: #d1d5db;
            background: #f9fafb;
          }

          &.selected {
            border-color: #10b981;
            background: #ecfdf5;
          }

          .emp-avatar {
            width: 36px;
            height: 36px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;

            &.new {
              background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
            }

            &.resigned {
              background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            }
          }

          .emp-info {
            flex: 1;
            min-width: 0;

            .emp-name {
              display: block;
              font-size: 0.875rem;
              font-weight: 600;
              color: #1f2937;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;

              .emp-birth {
                font-weight: 500;
                color: #6b7280;
              }
            }

            .emp-date {
              display: block;
              font-size: 0.75rem;
              color: #6b7280;
              margin-top: 2px;
            }
          }

          .check-mark {
            flex-shrink: 0;
          }
        }

        .no-employee-notice {
          grid-column: 1 / -1;
          padding: 24px;
          text-align: center;
          color: #9ca3af;
          font-size: 0.875rem;
          background: #f9fafb;
          border-radius: 10px;
          border: 1px dashed #e5e7eb;
        }
      }

      .memo-textarea {
        :deep(.v-field) {
          border-radius: 10px;
        }
      }
    }
  }

  .modal-footer {
    display: flex;
    gap: 12px;
    padding: 20px 24px;
    border-top: 1px solid #f1f3f5;
    background: #fafbfc;

    .btn-cancel,
    .btn-close {
      flex: 1;
      height: 48px;
      background: white;
      color: #4b5563;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      font-size: 0.938rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover:not(:disabled) {
        background: #f9fafb;
        border-color: #d1d5db;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .btn-submit {
      flex: 2;
      height: 48px;
      background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%);
      color: white;
      border: none;
      border-radius: 12px;
      font-size: 0.938rem;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 8px rgba(30, 58, 138, 0.25);

      &:hover:not(:disabled) {
        background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
        box-shadow: 0 4px 12px rgba(30, 58, 138, 0.35);
        transform: translateY(-1px);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
      }
    }

    .btn-cancel-request {
      flex: 1;
      height: 48px;
      background: white;
      color: #ef4444;
      border: 2px solid #ef4444;
      border-radius: 12px;
      font-size: 0.938rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover:not(:disabled) {
        background: #fef2f2;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}

.cancel-confirm-dialog {
  border-radius: 16px !important;
  overflow: hidden;

  .dialog-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 28px 24px 20px;

    &.warning {
      background: #fffbeb;
    }

    h3 {
      font-size: 1.125rem;
      font-weight: 700;
      color: #191f28;
      margin: 0;
    }
  }

  .dialog-body {
    padding: 20px 24px;
    text-align: center;

    p {
      font-size: 0.938rem;
      color: #4b5563;
      margin: 0 0 16px 0;
    }

    .cancel-reason-input {
      :deep(.v-field) {
        border-radius: 10px;
      }
    }
  }

  .dialog-footer {
    display: flex;
    gap: 12px;
    padding: 16px 24px 20px;

    .btn-dialog-cancel,
    .btn-dialog-confirm {
      flex: 1;
      height: 44px;
      border-radius: 10px;
      font-size: 0.875rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .btn-dialog-cancel {
      background: white;
      color: #4b5563;
      border: 1px solid #e5e7eb;

      &:hover:not(:disabled) {
        background: #f9fafb;
      }
    }

    .btn-dialog-confirm {
      border: none;
      color: white;

      &.warning {
        background: #f59e0b;

        &:hover:not(:disabled) {
          background: #d97706;
        }
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}

.mr-1 {
  margin-right: 4px;
}

.mr-2 {
  margin-right: 8px;
}
</style>

